import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search as SearchIcon, Filter, Building2, Wine, Store, Utensils } from "lucide-react";
import { api, STAGES, ESTAB_TYPES } from "../lib/api";
import { PageHeader, Spinner, EmptyState } from "../components/UI";
import StageBadge from "../components/StageBadge";
import { fmtDateShort } from "../lib/utils";

const TYPE_ICONS = { restaurant: Utensils, bar: Wine, liquor_store: Store };

export default function Establishments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (stageFilter) params.pipeline_stage = stageFilter;
      if (typeFilter) params.type = typeFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/establishments", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, [stageFilter, typeFilter]);

  return (
    <div>
      <PageHeader
        testid="establishments-header"
        title="Establishments"
        subtitle="Every restaurant, bar, and store you're working on."
        actions={
          <Link to="/establishments/new" className="btn-primary" data-testid="add-establishment-button">
            <Plus size={16} /> New
          </Link>
        }
      />

      <div className="card p-4 mb-4">
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="flex flex-col md:flex-row gap-3"
        >
          <div className="relative flex-1">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mutedFg" />
            <input
              data-testid="search-input"
              className="input-base pl-10"
              placeholder="Search by name, city, address…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="filter-stage"
            className="input-base md:w-44"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
          >
            <option value="">All stages</option>
            {STAGES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            data-testid="filter-type"
            className="input-base md:w-44"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All types</option>
            {ESTAB_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <button type="submit" className="btn-outline" data-testid="search-apply">
            <Filter size={16} /> Apply
          </button>
        </form>
      </div>

      {loading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <EmptyState
          testid="empty-establishments"
          icon={Building2}
          title="No establishments yet"
          body="Add accounts manually or use Discover to import nearby spots in one click."
          action={
            <div className="flex gap-2 justify-center">
              <Link to="/establishments/new" className="btn-primary">
                <Plus size={16} /> New establishment
              </Link>
              <Link to="/search" className="btn-outline">Discover nearby</Link>
            </div>
          }
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="establishments-grid">
          {items.map((e) => {
            const Icon = TYPE_ICONS[e.type] || Building2;
            return (
              <Link
                to={`/establishments/${e.id}`}
                key={e.id}
                data-testid={`estab-card-${e.id}`}
                className="card p-5 hover:-translate-y-0.5 transition-transform"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-muted flex items-center justify-center text-brand-ink">
                      <Icon size={18} strokeWidth={1.6} />
                    </div>
                    <div>
                      <div className="font-display text-lg text-brand-ink leading-tight">{e.name}</div>
                      <div className="text-xs text-brand-mutedFg capitalize">{e.type.replace("_", " ")}</div>
                    </div>
                  </div>
                  <StageBadge stage={e.pipeline_stage} />
                </div>
                <div className="mt-4 text-sm text-brand-mutedFg space-y-1">
                  {(e.city || e.state) && <div>{[e.city, e.state].filter(Boolean).join(", ")}</div>}
                  {e.address && <div className="truncate">{e.address}</div>}
                  <div className="text-xs">Added {fmtDateShort(e.created_at)}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
